/**
 * Balance Utilities
 * Handles transformation and calculation of time off balances
 */

export interface Balance {
  id: string
  title: string
  current?: string
  future?: string
  available?: string
  notAvailable?: boolean
  birthdayStatus?: string
}

export interface TimeOffBalanceRecord {
  current?: number
  futureRequests?: number
  available?: number
  notAvailable?: boolean
  eligible?: boolean
  status?: string
}

export interface TimeOffBalanceData {
  vacation: TimeOffBalanceRecord
  float: TimeOffBalanceRecord
  fpd: TimeOffBalanceRecord
  birthday?: TimeOffBalanceRecord
}

export interface HoursValues {
  vacation: string
  float: string
  flexible: string
  other: string
}

export interface DeductedBalances {
  vacation: string
  float: string
  flexible: string
  other: string
}

type BalanceLikeInput =
  | Balance[]
  | Record<string, string | number | { available?: string | number }>

const BALANCE_TYPES = ['vacation', 'float', 'flexible', 'other'] as const


const normalizeBalanceInput = (
  resultingBalances: BalanceLikeInput
): Balance[] => {
  if (Array.isArray(resultingBalances)) {
    return resultingBalances
  }

  return BALANCE_TYPES.map((type) => {
    const raw = resultingBalances?.[type]
    if (raw == null) {
      return { id: type, title: type, available: 'N/A' }
    }
    if (typeof raw === 'number' || typeof raw === 'string') {
      return { id: type, title: type, available: String(raw) }
    }
    return {
      id: type,
      title: type,
      available: raw.available == null ? 'N/A' : String(raw.available),
    }
  })
}

const BALANCE_MAPPING: Record<
  string,
  { dataKey: keyof TimeOffBalanceData; title: string }
> = {
  vacation: { dataKey: 'vacation', title: 'Vacation (PTO/ETO)' },
  float: { dataKey: 'float', title: 'Float' },
  flexible: { dataKey: 'fpd', title: 'Flexible Personal Days' },
  other: { dataKey: 'birthday', title: 'Other' },
}

/**
 * Transform raw TimeOffBalance API data to Balance array format
 * @param timeOffBalanceData Raw balance data from Redux
 * @returns Formatted Balance array ready for display
 */
export const transformTimeOffBalanceToBalances = (
  timeOffBalanceData: TimeOffBalanceData | null
): Balance[] => {
  if (!timeOffBalanceData) {
    return []
  }

  return BALANCE_TYPES.map((type) => {
    const config = BALANCE_MAPPING[type]
    const data = timeOffBalanceData[config.dataKey]

    if (!data) {
      return {
        id: type,
        title: config.title,
        current: '0',
        future: '0',
        available: 'N/A',
        notAvailable: false,
      }
    }

    return {
      id: type,
      title: config.title,
      current: data.current == null ? '0' : data.current.toFixed(2),
      future:
        data.futureRequests == null ? '0' : data.futureRequests.toString(),
      available: data.available == null ? 'N/A' : data.available.toString(),
      notAvailable: data.notAvailable ?? false,
    }
  })
}

/**
 * Calculate deducted balances assuming a single uniform per-day rate applied to
 * every selected date.
 * Deduction formula: available - (hoursPerDay × numberOfDates)
 *
 * Example:
 * - 3 dates selected, 8 hours per day entered → deduction = 8 × 3 = 24 hours
 * - 3 dates selected, 4 hours per day entered → deduction = 4 × 3 = 12 hours
 *
 * @remarks
 * This helper is only correct when **every** day carries the same hours. When
 * individual days can differ (e.g. after editing one date's hours), use
 * {@link calculateResultingBalances} with the true per-type totals instead —
 * otherwise the deduction is wrong (a single edited day would be multiplied
 * across all dates).
 *
 * @param resultingBalances The balance records with available amounts
 * @param hoursValues Current hours per day entered by user
 * @param numberOfDates Number of selected dates (default 1)
 * @returns Deducted balance amounts for each type
 */
export const calculateDeductedBalances = (
  resultingBalances: BalanceLikeInput,
  hoursValues: HoursValues,
  numberOfDates: number = 1
): DeductedBalances => {
  const deducted: DeductedBalances = {
    vacation: '0',
    float: '0',
    flexible: '0',
    other: '0',
  }

  // Ensure numberOfDates is at least 1 and a valid number
  const dateCount = Math.max(1, numberOfDates || 1)

  const normalizedBalances = normalizeBalanceInput(resultingBalances)

  BALANCE_TYPES.forEach((type) => {
    const balanceItem = normalizedBalances.find((b) => b.id === type)
    const availableStr = balanceItem?.available
    const hoursPerDayStr = hoursValues[type] || '0'

    if (availableStr === 'N/A' || !availableStr) {
      deducted[type] = 'N/A'
    } else {
      const available = Number.parseFloat(availableStr)
      const hoursPerDay = Number.parseFloat(hoursPerDayStr) || 0

      // Calculate total hours: hours per day × number of dates
      const totalHoursRequested = hoursPerDay * dateCount

      // Deduct from available (minimum 0)
      const deductedAmount = available - totalHoursRequested
      deducted[type] = deductedAmount.toFixed(2)
    }
  })

  return deducted
}

/**
 * Numeric total hours requested for each leave type, summed across every
 * selected date. Unlike a per-day rate, these are the actual cumulative hours
 * the user has entered (e.g. 8 + 8 + 2 = 18), allowing days with different
 * hours to be deducted accurately.
 */
export type BalanceHoursTotals = Record<(typeof BALANCE_TYPES)[number], number>

/**
 * Calculate resulting (remaining) balances by deducting the TOTAL hours actually
 * requested across all selected dates for each leave type.
 *
 * Unlike {@link calculateDeductedBalances} — which assumes one uniform per-day
 * rate applied to every date (`hoursPerDay × numberOfDates`) — this function
 * deducts the precise sum of each day's entered hours. That keeps the resulting
 * balance correct when individual days carry different hours, such as after the
 * user edits a single date, where a per-day × count formula would be wrong.
 *
 * Example (vacation available = 24):
 * - Days entered 8 + 8 + 2 → total 18 → resulting balance = 24 − 18 = 6.
 *   (A per-day × count formula on the edited "2" would wrongly compute
 *   2 × 3 = 6 deducted, leaving 18 — the bug this function fixes.)
 *
 * @param resultingBalances The balance records with available amounts
 * @param totalHoursByType Summed hours per leave type across all selected dates
 * @returns Remaining balance per type as a 2-decimal string, or 'N/A'
 */
export const calculateResultingBalances = (
  resultingBalances: BalanceLikeInput,
  totalHoursByType: BalanceHoursTotals
): DeductedBalances => {
  const resulting: DeductedBalances = {
    vacation: '0',
    float: '0',
    flexible: '0',
    other: '0',
  }

  const normalizedBalances = normalizeBalanceInput(resultingBalances)
  BALANCE_TYPES.forEach((type) => {
    const balanceItem = normalizedBalances.find((b) => b.id === type)
    const availableStr = balanceItem?.available

    // When a balance is unavailable, surface 'N/A' rather than a numeric result.
    if (availableStr === 'N/A' || !availableStr) {
      resulting[type] = 'N/A'
      return
    }

    const available = Number.parseFloat(availableStr)
    const totalRequested = totalHoursByType[type] || 0

    // Deduct the true cumulative hours for this leave type across every date.
    resulting[type] = (available - totalRequested).toFixed(2)
  })

  return resulting
}

/**
 * Format balance display for UI
 * @param balance The numeric balance value
 * @returns Formatted string for display
 */
export const formatBalanceDisplay = (balance: string | number): string => {
  if (typeof balance === 'string') {
    if (balance === 'N/A' || balance === '') {
      return 'N/A'
    }
    return balance
  }
  return Number(balance).toFixed(2)
}

/**
 * Check if a balance has insufficient hours available
 * @param available The available balance amount
 * @param requested The requested hours
 * @returns true if insufficient, false otherwise
 */
export const hasInsufficientBalance = (
  available: string | number,
  requested: number
): boolean => {
  if (available === 'N/A' || available === '') {
    return false
  }
  const availableNum = Number.parseFloat(String(available)) || 0
  return availableNum < requested
}

/**
 * Check if birthday balance is available in the timeOffBalance data
 * @param timeOffBalanceData Raw balance data from Redux
 * @returns true if birthday is eligible (should be shown), false otherwise
 */
export const isBirthdayAvailable = (
  timeOffBalanceData: TimeOffBalanceData | null
): boolean => {
  if (!timeOffBalanceData?.birthday) {
    return false
  }
  // Check if birthday.eligible is explicitly false
  return timeOffBalanceData.birthday.eligible !== false
}

/**
 * Check if birthday has already been taken or is already requested
 * @param timeOffBalanceData Raw balance data from Redux
 * @returns true if birthday status is "Taken" or "Requested", false otherwise
 */
export const isBirthdayUnavailable = (
  timeOffBalanceData: TimeOffBalanceData | null
): boolean => {
  if (!timeOffBalanceData?.birthday) {
    return false
  }
  return (
    timeOffBalanceData.birthday.status === 'Taken' ||
    timeOffBalanceData.birthday.status === 'Requested'
  )
}

/**
 * Get birthday balance status
 * @param timeOffBalanceData Raw balance data from Redux
 * @returns Birthday balance info or null if not available
 */
export const getBirthdayBalance = (
  timeOffBalanceData: TimeOffBalanceData | null
): TimeOffBalanceRecord | null => {
  if (!timeOffBalanceData?.birthday) {
    return null
  }
  return timeOffBalanceData.birthday
}

/**
 * Check if birthday balance has available hours (eligible and not taken/requested)
 * @param timeOffBalanceData Raw balance data from Redux
 * @returns true if birthday can be used (eligible and not taken or requested), false otherwise
 */
export const hasBirthdayHours = (
  timeOffBalanceData: TimeOffBalanceData | null
): boolean => {
  if (!timeOffBalanceData?.birthday) {
    return false
  }
  // Birthday is available if it's eligible and not taken or requested
  const birthday = timeOffBalanceData.birthday
  const isEligible = birthday.eligible !== false
  const isNotTaken =
    birthday.status !== 'Taken' && birthday.status !== 'Requested'
  return isEligible && isNotTaken
}

export { BALANCE_TYPES }
